<script setup lang="ts">
import { withDefaults, ref, watch, computed } from 'vue'
import {
	CollapsibleRoot,
	CollapsibleTrigger,
	CollapsibleContent,
} from 'radix-vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import type { FAQContent, FaqItem, ThemeConfig } from '@/types/faq'

interface Props {
	content?: FAQContent
	theme:
		| 'shopping'
		| 'shipping'
		| 'shipping-index'
		| 'mercari-index'
		| 'proxy-shopping'
}

const props = withDefaults(defineProps<Props>(), {
	content: () => ({
		sectionTitle: 'Frequently asked questions',
		questions: 'What is the coverage for lost or damaged parcels?',
		CTAButton: { text: 'Help centre', link: '/contact-us' },
		faqItems: [],
		linkCards: [],
	}),
})

const localFaqItems = ref<FaqItem[]>([])

const themeConfig = computed((): ThemeConfig => {
	switch (props.theme) {
		case 'shopping':
			return {
				pageName: 'Shopping Protection Page',
				buttonNameStrategy: 'letter' as const,
			}
		case 'shipping':
			return {
				pageName: 'Shipment Protection Page',
				buttonNameStrategy: 'letter' as const,
			}
		case 'shipping-index':
			return {
				pageName: GaGeneralPageName.Shipping,
				buttonNameStrategy: 'custom' as const,
				buttonNames: [
					'Prohibited Items',
					'Overseas Warehouse',
					'Protection Plan',
				],
			}
		case 'mercari-index':
			return {
				pageName: GaGeneralPageName.MercariIndex,
				buttonNameStrategy: 'letter' as const,
			}
		case 'proxy-shopping':
			return {
				pageName: GaGeneralPageName.ProxyShopping,
				buttonNameStrategy: 'letter' as const,
			}
		default:
			return {
				pageName: 'Protection Page',
				buttonNameStrategy: 'letter' as const,
			}
	}
})

const handleHelpCentreClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: themeConfig.value.pageName,
		button_name: 'Help Centre - FAQ',
	})
}

const handleFAQExpand = (isExpanded: boolean) => {
	if (isExpanded) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: themeConfig.value.pageName,
			button_name:
				themeConfig.value.pageName === GaGeneralPageName.Shipping ||
				themeConfig.value.pageName === GaGeneralPageName.MercariIndex
					? 'FAQ'
					: 'Expand - FAQ',
		})
	}
}

const generateButtonName = (cardIndex: number) => {
	const config = themeConfig.value

	if (config.buttonNameStrategy === 'custom' && config.buttonNames) {
		const customNames = config.buttonNames
		return customNames[cardIndex]
	}

	const letters = ['A', 'B', 'C', 'D', 'E', 'F']
	const letter = letters[cardIndex] || `${cardIndex + 1}`
	return `Button ${letter}`
}

const handleLinkCardClick = (cardIndex: number) => {
	const buttonName = generateButtonName(cardIndex)

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: themeConfig.value.pageName,
		button_name: `${buttonName} - FAQ`,
	})
}

const getItemClassName = (type: string) => {
	switch (type) {
		case 'ordered':
			return 'list-outside list-decimal pl-20px'
		case 'unordered':
			return 'list-outside list-disc pl-20px'
		case 'paragraph':
			return ''
		default:
			return ''
	}
}

const getContentClassName = (content: any, hasSubItems = false) => {
	return 'typography-b2-reg text-n-500'
}

const getParagraphClassName = () => {
	return 'space-y-12px'
}

watch(
	() => props.content?.faqItems,
	newItems => {
		if (newItems) {
			localFaqItems.value = newItems.map(item => ({ ...item }))
		}
	},
	{ immediate: true },
)
</script>

<template>
	<div class="flex flex-col gap-acquaintances">
		<main
			class="flex flex-col items-center justify-center gap-20px mdp:flex-row mdp:items-start mdp:justify-between"
		>
			<!-- left -->
			<div
				class="flex flex-col items-center justify-center gap-16px mdp:mr-24px mdp:w-[282px] mdp:items-start lg:w-[384px]"
			>
				<h2 class="typography-h2-str text-center mdp:text-left">
					{{ props.content?.sectionTitle }}
				</h2>

				<div class="flex flex-col gap-16px">
					<p class="typography-b2-reg text-n-800">
						{{ props.content?.questions }}
					</p>

					<a
						:href="props.content?.CTAButton?.link"
						class="mx-auto mt-8px w-auto mdp:mx-0"
					>
						<BaseButton
							variant="primary"
							size="medium"
							@click="handleHelpCentreClick"
						>
							{{ props.content?.CTAButton?.text || 'Help centre' }}
						</BaseButton>
					</a>
				</div>
			</div>

			<!-- right -->
			<div class="flex w-full flex-1 flex-col">
				<CollapsibleRoot
					v-for="(item, index) in localFaqItems"
					:key="index"
					v-model:open="item.isOpen"
					class="bg-transparent"
					@update:open="(isOpen: boolean) => handleFAQExpand(isOpen)"
				>
					<CollapsibleTrigger as-child>
						<button
							:class="[
								'group w-full text-left',
								index === 0 ? 'pb-24px' : 'py-24px',
								!item.isOpen ? 'border-b border-n-100' : '',
							]"
						>
							<div class="flex items-center justify-between gap-16px">
								<h3 class="typography-h5-str min-w-0 flex-1">
									{{ item.question }}
								</h3>

								<BaseIcon
									:name="item.isOpen ? 'minus_v1' : 'add_v1'"
									class="h-24px w-24px flex-shrink-0 fill-o-500"
								/>
							</div>
						</button>
					</CollapsibleTrigger>

					<CollapsibleContent
						v-if="item.answer"
						class="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down"
						:style="{ willChange: 'height' }"
					>
						<div class="pb-0">
							<div class="space-y-16px">
								<div
									v-for="(answerItem, answerIndex) in item.answer.items"
									:key="answerIndex"
								>
									<!-- ordered list -->
									<ol
										v-if="answerItem.type === 'ordered'"
										:class="getItemClassName(answerItem.type)"
									>
										<li
											v-for="(content, contentIndex) in answerItem.content"
											:key="contentIndex"
											:class="
												getContentClassName(
													content,
													!!(typeof content === 'object' && content.subItems),
												)
											"
										>
											<!-- main item text -->
											<span>
												{{
													typeof content === 'string' ? content : content.text
												}}
											</span>

											<!-- sub item list -->
											<div
												v-if="typeof content === 'object' && content.subItems"
												class=""
											>
												<ol
													v-if="content.subItems.type === 'ordered'"
													class="list-outside list-decimal pl-20px"
												>
													<li
														v-for="(subItem, subIndex) in content.subItems
															.items"
														:key="subIndex"
														class="typography-b2-reg text-n-500"
													>
														{{ subItem }}
													</li>
												</ol>
												<ul v-else class="list-outside list-disc pl-20px">
													<li
														v-for="(subItem, subIndex) in content.subItems
															.items"
														:key="subIndex"
														class="typography-b2-reg text-n-500"
													>
														{{ subItem }}
													</li>
												</ul>
											</div>
										</li>
									</ol>

									<!-- unordered list -->
									<ul
										v-else-if="answerItem.type === 'unordered'"
										:class="getItemClassName(answerItem.type)"
									>
										<li
											v-for="(content, contentIndex) in answerItem.content"
											:key="contentIndex"
											:class="
												getContentClassName(
													content,
													!!(typeof content === 'object' && content.subItems),
												)
											"
										>
											<!-- main item text -->
											<span>
												{{
													typeof content === 'string' ? content : content.text
												}}
											</span>

											<!-- sub item list -->
											<div
												v-if="typeof content === 'object' && content.subItems"
												class=""
											>
												<ol
													v-if="content.subItems.type === 'ordered'"
													class="list-outside list-decimal pl-20px"
												>
													<li
														v-for="(subItem, subIndex) in content.subItems
															.items"
														:key="subIndex"
														class="typography-b2-reg text-n-500"
													>
														{{ subItem }}
													</li>
												</ol>
												<ul v-else class="list-outside list-disc pl-20px">
													<li
														v-for="(subItem, subIndex) in content.subItems
															.items"
														:key="subIndex"
														class="typography-b2-reg text-n-500"
													>
														{{ subItem }}
													</li>
												</ul>
											</div>
										</li>
									</ul>

									<!-- paragraph -->
									<div
										v-else-if="answerItem.type === 'paragraph'"
										:class="getParagraphClassName()"
									>
										<div
											v-for="(content, contentIndex) in answerItem.content"
											:key="contentIndex"
										>
											<!-- main paragraph text -->
											<p
												:class="
													getContentClassName(
														content,
														!!(typeof content === 'object' && content.subItems),
													)
												"
											>
												{{
													typeof content === 'string' ? content : content.text
												}}
											</p>

											<!-- sub item list -->
											<div
												v-if="typeof content === 'object' && content.subItems"
												class="ml-16px mt-8px"
											>
												<ol
													v-if="content.subItems.type === 'ordered'"
													class="list-outside list-decimal pl-20px"
												>
													<li
														v-for="(subItem, subIndex) in content.subItems
															.items"
														:key="subIndex"
														class="typography-b2-reg text-n-500"
													>
														{{ subItem }}
													</li>
												</ol>
												<ul v-else class="list-outside list-disc pl-20px">
													<li
														v-for="(subItem, subIndex) in content.subItems
															.items"
														:key="subIndex"
														class="typography-b2-reg text-n-500"
													>
														{{ subItem }}
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</CollapsibleContent>

					<div v-if="item.isOpen" class="border-b border-n-100 pb-24px" />
				</CollapsibleRoot>
			</div>
		</main>

		<footer class="grid grid-cols-1 gap-20px mdp:grid-cols-3">
			<a
				v-for="(card, cardIndex) in props.content?.linkCards"
				:key="card.title"
				:href="card.href"
				target="_blank"
				class="group rounded-lg border border-transparent bg-o-75 p-good-friends !no-underline hover:border hover:border-n-950"
				@click="handleLinkCardClick(cardIndex)"
			>
				<div class="flex items-start justify-between">
					<div class="space-y-16px">
						<h5 class="typography-h5-str text-n-950">
							{{ card.title }}
						</h5>
						<p class="typography-b2-reg text-n-800">
							{{ card.description }}
						</p>
					</div>

					<div class="ml-16px flex-shrink-0">
						<BaseIcon name="arrow-goto" class="h-24px w-24px text-o-500" />
					</div>
				</div>
			</a>
		</footer>
	</div>
</template>
